﻿import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { CrudFunctions } from '../../utilities/crud-functions';
import { ProductStandard } from '../../models/product-standard';
import { AnalyticalPlanStandard } from '../../models/analytical-plan-standard';
import { Entities, ModalMessageType } from '../../models/enums';
import { ItemBasicInformation } from '../../models/item-basic-information';
import AuditHistoryComponent from '../AuditHistoryComponent';
import ItemBasicInformationDialog from '../ItemBasicInformationDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import GlobalDialog from '../Modal/GlobalDialog';
import { StandardsUtil } from '../../utilities/standards-util';

import DataGrid, {
    Column,
    Paging,
    Toolbar,
    Selection,
    Editing,
    Popup,
    Form,
    Lookup,
    Button as ColumnButton,
    Item as ToolbarItem
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import Button from 'devextreme-react/button';
import { Item } from 'devextreme-react/form';

let key = 0;

const productStandardTypes = [
    { id: 1, name: 'Qualitative' },
    { id: 2, name: 'Quantitative' }
]

export interface Props {
    productId: string;
    isReadOnly: boolean;
    title?: string;
}

export default function BatchStandardsData(props: Props) {
    const [productStandards, setProductStandards] =
        useState<ProductStandard[]>();

    const [selectedProductStandard, setSelectedProductStandard] =
        useState<ProductStandard>();

    const [analyticalPlanStandards, setAnalyticalPlanStandards] =
        useState<AnalyticalPlanStandard[]>();

    const [auditRowPointer, setAuditRowPointer] =
        useState<string>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    useEffect(() => {
        getAllData()
    }, [props.productId]);

    key++;

    return (
        <div>
            <div>
                <DataGrid height="16rem"
                    dataSource={productStandards ? productStandards : []}
                    showBorders={true}
                    allowColumnResizing={true}
                    wordWrapEnabled={true}
                    onSelectionChanged={onProductStandardSelectionChanged}
                    onRowValidating={onProductStandardValidating}
                    onRowRemoving={onProductStandardRemoving}
                    columnResizingMode="nextColumn"

                    rowAlternationEnabled={true}
                    showRowLines={true} >

                    <Paging enabled={false} />
                    <Selection mode='single' />

                    <Column type="buttons" width='7rem' >
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onStandardAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onStandardInformationButtonClicked} />
                        <ColumnButton hint="edit standard" name="edit" />
                        <ColumnButton hint="remove standard" name="delete" />
                    </Column>

                    <Column dataField='manufacturerName'
                        width='8rem' caption="Manufacturer" />

                    <Column dataField='productName' />

                    <Column dataField='catalogNumber' width='7rem' caption="Catalog #" />

                    <Column dataField="catalogReference" caption="" alignment="center"
                        width='2rem' cssClass="table-vertical-align"
                        cellRender={catalogReferenceCellRender} />

                    <Column dataField='netWeightString' caption="Net Weight" width='7rem' dataType="string" />

                    <Column dataField='standardTypeId' caption="Type" dataType="number" width="7rem" >
                        <Lookup dataSource={productStandardTypes ? productStandardTypes : []}
                            valueExpr='id' displayExpr='name' />
                    </Column>

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Batch Standards</div>
                            </div>
                        </ToolbarItem>
                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowAdding={false}
                        allowUpdating={!props.isReadOnly}
                        allowDeleting={false}>

                        <Popup title="Batch Standard" showTitle={true} width="25rem" height="12rem" />
                        <Form>
                            <Item dataField="lotNumber"  />
                        </Form>

                    </Editing>
                </DataGrid>

            </div>

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={Entities.CompStandards}
                        key={`AuditHistory${key}`}
                        childData={false}
                        rowPointer={auditRowPointer ? auditRowPointer : undefined} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onProductStandardSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedProductStandard(e.selectedRowsData[0]);
    }

    async function onProductStandardRemoving(e: any) {
        if (e.data && e.data.productId && e.data.standardId) {
            await deleteProductStandard(e.data.productId, e.data.standardId)
                .then(() => {
                    getProductStandardsData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Product Standard");
                });
        }
    }

    async function onProductStandardValidating(e: any) {
        if (e.oldData) {
            let standard = e.oldData as ProductStandard;

            if (standard) {
                let updatedStandard = { ...standard, ...e.newData };

                await updateProductStandard(updatedStandard)
                    .then(() => {
                        getProductStandardsData();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Product Standard");
                    });
            }
        }
    }

    async function getProductStandardsData() {
        await CrudFunctions.getData(`products/standards/${props.productId}`)
            .then((data) => {
                setProductStandards(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Product Standards");
            });
    }

    async function getAnalyticalPlansStandardsData() {
        await CrudFunctions.getData(`products/analyticalplanstandards/${props.productId}`)
            .then((data) => {

                console.log("AP Standards");
                console.log(data);

                setAnalyticalPlanStandards(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Analytical Plan Standards");
            });
    }

    async function updateProductStandard(standard: ProductStandard) {
        return await CrudFunctions.putData("products/productstandard", standard);
    }

    async function deleteProductStandard(productId: string, standardId: string) {
        return await CrudFunctions.deleteData(`products/productstandard/${productId}/${standardId}`);
    }

    async function getAllData() {
        await Promise.all([
            getProductStandardsData(),
            getAnalyticalPlansStandardsData()
        ]);
    }

    //render

    function catalogReferenceCellRender(e: any) {
        if (e.data) {
            let standard = e.data as ProductStandard;

            if (standard && standard.manufacturerId && standard.catalogNumber && standard.currentLotNumber) {

                const path = StandardsUtil.standardCoaUrl(standard.manufacturerId, standard.catalogNumber, standard.currentLotNumber);

                if (path) {
                    return <NavLink to={path} className="datagrid-icon-link"
                        target="_blank" rel="noopener noreferrer">
                        <div className="dx-icon-globe datagrid-icon"></div>
                    </NavLink>;
                }
            }
        }

        return <div></div>;
    }

    function onStandardAuditButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setAuditRowPointer(e.row.data.productStandardId);
            setAuditInformationDialogVisible(true);
        }
    }

    function onStandardInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.productStandardId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    
}